import type { StateCreator } from 'zustand';
import { getSafeErrorFields } from '../errors';
import { type AnalyzerResult } from './analyzerSlice';
import type { AudioGraphStore } from './types';

export type TaskFn = (signal?: AbortSignal) => Promise<void> | void;

export type AudioGraphTaskType =
  | 'START'
  | 'AWAIT_GRAPH_READY'
  | 'REBUILD_GRAPH'
  | 'CHECK_DEVICES'
  | 'CHANGE_DEVICE_ID'
  | 'GET_MIC_ANALYZER'
  | 'GET_TAB_ANALYZER'
  | 'RELEASE_MIC_ANALYZER'
  | 'RELEASE_TAB_ANALYZER';

export interface Task {
  type: AudioGraphTaskType;
  promise: TaskFn;
  resolve: () => void;
  reject: (error: unknown) => void;
}

export interface TaskQueueSlice {
  processingTask: AudioGraphTaskType | null;
  activeTaskController: AbortController | null;
  taskQueue: Task[];
  isPaused: boolean;
  enqueueTask: (type: AudioGraphTaskType, promise: TaskFn) => Promise<void>;
  processTask: (singleTask?: boolean) => Promise<void>;
  abortActiveTask: (reason?: unknown) => void;
  toggleIsPaused: () => void;

  // Task wrappers.
  startTask: () => Promise<void>;
  rebuildGraphTask: (disconnectMicAnalyzer?: boolean) => Promise<void>;
  changeDeviceIdTask: (deviceId: string) => Promise<void>;
  checkDevicesTask: () => Promise<void>;

  getMicAnalyzerTask: () => Promise<AnalyzerResult>;
  getTabAnalyzerTask: () => Promise<AnalyzerResult>;
  releaseMicAnalyzerTask: (id: string) => Promise<void>;
  releaseTabAnalyzerTask: (id: string) => Promise<void>;
}

const dedupList: AudioGraphTaskType[] = ['START', 'REBUILD_GRAPH', 'CHECK_DEVICES'];
const loggingIgnoreList: AudioGraphTaskType[] = ['CHECK_DEVICES', 'REBUILD_GRAPH'];

export const createTaskQueueSlice: StateCreator<
  AudioGraphStore,
  [],
  [],
  TaskQueueSlice
> = (set, get) => ({
  processingTask: null,
  activeTaskController: null,
  taskQueue: [],
  isPaused: false,

  enqueueTask: async (type: AudioGraphTaskType, promise: TaskFn) => {
    if (dedupList.includes(type) && get().taskQueue.some((t) => t.type === type)) return;

    return new Promise((resolve, reject) => {
      const operation: Task = { type, promise, resolve, reject };
      set((state) => ({ taskQueue: [...state.taskQueue, operation] }));
      void get().processTask();
    });
  },

  processTask: async (singleTask = false) => {
    const { logDebug, logError, processTask } = get();
    if (
      get().processingTask ||
      get().taskQueue.length === 0 ||
      (get().isPaused && !singleTask)
    )
      return;
    const [nextTask, ...remainingTasks] = get().taskQueue;
    set({ taskQueue: remainingTasks, processingTask: nextTask?.type });
    if (!nextTask) return;
    const { type } = nextTask;
    const logging = !loggingIgnoreList.includes(type);
    const taskController = new AbortController();
    set({ activeTaskController: taskController });

    try {
      if (logging) logDebug({ event_type: 'PROCESS_TASK_START', details: { type } });
      await nextTask.promise(taskController.signal);
      if (logging) logDebug({ event_type: 'PROCESS_TASK_SUCCESS', details: { type } });
      nextTask.resolve();
    } catch (err) {
      nextTask.reject(err);
      logError({
        event_type: 'PROCESS_TASK_ERROR',
        details: { type, ...getSafeErrorFields(err) },
      });
    } finally {
      set({ processingTask: null, activeTaskController: null });
      if (!singleTask) void processTask();
    }
  },

  abortActiveTask: (reason) => {
    get().activeTaskController?.abort(reason);
  },

  toggleIsPaused: () => {
    set({ isPaused: !get().isPaused });
    if (!get().isPaused) void get().processTask();
  },

  // Task wrappers.
  startTask: () => get().enqueueTask('START', (signal) => get().start(signal)),
  rebuildGraphTask: (disconnectMicAnalyzer) => {
    return get().enqueueTask('REBUILD_GRAPH', (signal) =>
      get().rebuildGraph(disconnectMicAnalyzer, signal)
    );
  },
  checkDevicesTask: () =>
    get().enqueueTask('CHECK_DEVICES', (signal) => get().checkDevices(signal)),

  changeDeviceIdTask: async (deviceId) => {
    return get().enqueueTask('CHANGE_DEVICE_ID', (signal) =>
      get().changeDeviceId(deviceId, signal)
    );
  },

  getMicAnalyzerTask: async () => {
    let result: AnalyzerResult | undefined;

    await get().enqueueTask('GET_MIC_ANALYZER', () => {
      result = get().getMicAnalyzer();
    });

    return result!;
  },

  getTabAnalyzerTask: async () => {
    let result: AnalyzerResult | undefined;

    await get().enqueueTask('GET_TAB_ANALYZER', () => {
      result = get().getTabAnalyzer();
    });

    return result!;
  },

  releaseMicAnalyzerTask: (id: string) => {
    return get().enqueueTask('RELEASE_MIC_ANALYZER', () => {
      return get().releaseMicAnalyzer(id);
    });
  },

  releaseTabAnalyzerTask: (id: string) => {
    return get().enqueueTask('RELEASE_TAB_ANALYZER', () => {
      return get().releaseTabAnalyzer(id);
    });
  },
});
